import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import ReactPlayer from "react-player";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  Descriptions,
  Empty,
  Image,
  List,
  Modal,
  Space,
  Tag,
  Typography,
} from "antd";
import { ArrowLeftOutlined, PlayCircleOutlined } from "@ant-design/icons";
import Constants from "../Utils/Constants";

const { Title, Text } = Typography;

const api = axios.create({
  baseURL: Constants.SERVER_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const STATUS_COLOR = {
  DRAFT: "default",
  ACTIVE: "green",
  PAUSED: "orange",
  DONE: "blue",
};

const WEEKDAY_TO_CODE = {
  DAY_ONE: "mon",
  DAY_TWO: "tue",
  DAY_THREE: "wed",
  DAY_FOUR: "thu",
  DAY_FIVE: "fri",
  DAY_SIX: "sat",
  DAY_SEVEN: "sun",
};

const CODE_TO_LABEL = {
  mon: "Lundi",
  tue: "Mardi",
  wed: "Mercredi",
  thu: "Jeudi",
  fri: "Vendredi",
  sat: "Samedi",
  sun: "Dimanche",
};

const TIME_OF_DAY_LABELS = {
  MORNING: "Matin",
  AFTERNOON: "Après-midi",
  EVENING: "Soir",
};

function getCycleDurationLabel(startDate, endDate) {
  if (!startDate || !endDate) return null;

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  if (end.getTime() < start.getTime()) return null;

  const diffMs = end.getTime() - start.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1;

  const weeks = Math.floor(diffDays / 7);
  const days = diffDays % 7;

  const parts = [];
  if (weeks > 0) parts.push(`${weeks} semaine${weeks > 1 ? "s" : ""}`);
  if (days > 0) parts.push(`${days} jour${days > 1 ? "s" : ""}`);

  return parts.join(" et ") || "0 jour";
}

function FrequencyBadge({ frequency }) {
  if (!frequency) return null;

  const tod =
    frequency.timeOfDay === "MORNING"
      ? " • matin"
      : frequency.timeOfDay === "AFTERNOON"
      ? " • après-midi"
      : frequency.timeOfDay === "EVENING"
      ? " • soir"
      : "";

  if (frequency.kind === "free") {
    return (
      <Tag>
        {frequency.amount}{" "}
        {frequency.unit === "per_day" ? "/ jour" : "/ semaine"}
        {tod}
      </Tag>
    );
  }

  if (frequency.kind === "custom") {
    const order = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
    const txt = (frequency.days || [])
      .slice()
      .sort((a, b) => order.indexOf(a) - order.indexOf(b))
      .map((day) => CODE_TO_LABEL[day] || day)
      .join(", ");

    return (
      <Tag color="blue">
        Jours : {txt || "—"}
        {tod}
      </Tag>
    );
  }

  return null;
}

function buildCycleItemsFromData(data) {
  const map = new Map();

  (data?.SessionDays || data?.sessions || []).forEach((sd) => {
    const sessionId = sd?.SessionId ?? sd?.sessionId ?? sd?.id;
    const title = sd?.Session?.name ?? sd?.title ?? sd?.name ?? "Séance";
    const dayCode = WEEKDAY_TO_CODE[sd?.weekDay] ?? null;
    const timeOfDay = sd?.timeOfDay ?? null;

    if (!sessionId) return;

    if (!map.has(sessionId)) {
      map.set(sessionId, {
        title,
        days: new Set(),
        timeOfDaySet: new Set(),
      });
    }

    if (dayCode) map.get(sessionId).days.add(dayCode);
    if (timeOfDay) map.get(sessionId).timeOfDaySet.add(timeOfDay);
  });

  const grouped = Array.from(map.entries()).map(
    ([sessionId, { title, days, timeOfDaySet }]) => {
      const timeOfDay =
        timeOfDaySet.size === 1 ? Array.from(timeOfDaySet)[0] : null;

      return {
        baseSessionId: sessionId,
        title,
        frequency: {
          kind: "custom",
          days: Array.from(days),
          timeOfDay,
        },
      };
    }
  );

  const flexibleSessions = (data?.WeeklyCycleFlexibleSessions || []).map(
    (fs) => ({
      baseSessionId: fs?.SessionId,
      title: fs?.Session?.name ?? "Séance libre",
      frequency: {
        kind: "free",
        amount: fs.amount,
        unit: fs.unit,
        timeOfDay: fs?.timeOfDay ?? null,
      },
    })
  );

  return [...grouped, ...flexibleSessions];
}

function summarizeCycleFrequency(items) {
  if (!items || items.length === 0) return null;

  const kinds = new Set(
    items
      .map((item) => item.frequency?.kind)
      .filter((kind) => kind === "custom" || kind === "free")
  );

  if (kinds.size === 1 && kinds.has("custom")) {
    const daysSet = new Set();
    const timeOfDaySet = new Set();

    items.forEach((item) => {
      (item.frequency?.days || []).forEach((day) => daysSet.add(day));
      if (item.frequency?.timeOfDay) {
        timeOfDaySet.add(item.frequency.timeOfDay);
      }
    });

    const days = Array.from(daysSet);
    const timeOfDay =
      timeOfDaySet.size === 1 ? Array.from(timeOfDaySet)[0] : null;

    return {
      kind: "custom",
      days,
      timeOfDay,
    };
  }

  if (kinds.size === 1 && kinds.has("free")) {
    const first = items[0].frequency;

    const allSame = items.every((item) => {
      const frequency = item.frequency || {};
      return (
        frequency.amount === first.amount &&
        frequency.unit === first.unit &&
        frequency.timeOfDay === first.timeOfDay
      );
    });

    if (allSame) {
      return {
        kind: "free",
        amount: first.amount,
        unit: first.unit,
        timeOfDay: first.timeOfDay ?? null,
      };
    }
  }

  return { kind: "mixed" };
}

export default function ProgrammeDetails() {
  const { programmeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [programme, setProgramme] = useState(null);
  const [loading, setLoading] = useState(true);

  const [programmeContent, setProgrammeContent] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [sessionFrequencies, setSessionFrequencies] = useState({});
  const [resolvedMediaUrls, setResolvedMediaUrls] = useState({});

  const [videoPreviewOpen, setVideoPreviewOpen] = useState(false);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);

  const patientNameFromState = location.state?.patientName;

  const apiBase = useMemo(() => {
    const base = api?.defaults?.baseURL || Constants.SERVER_URL;
    return String(base).replace(/\/$/, "");
  }, []);

  const toAbsoluteUrl = (maybeUrl) => {
    if (!maybeUrl || typeof maybeUrl !== "string") return null;
    if (/^https?:\/\//i.test(maybeUrl)) return maybeUrl;
    if (maybeUrl.startsWith("/")) return `${apiBase}${maybeUrl}`;
    return `${apiBase}/${maybeUrl}`;
  };

  const isMinioImageKey = (value) =>
    typeof value === "string" && value.startsWith("images/");

  const isMinioVideoKey = (value) =>
    typeof value === "string" &&
    (value.startsWith("videos/") || value.startsWith("sessions/"));

  const resolveMediaUrl = async (value) => {
    if (!value || typeof value !== "string") return null;

    if (/^https?:\/\//i.test(value)) {
      return value;
    }

    if (isMinioImageKey(value)) {
      try {
        const { data } = await api.get("/api/minio/presign-download", {
          params: {
            bucket: "rxapa-assets",
            key: value,
          },
        });

        return data?.downloadUrl || null;
      } catch (err) {
        console.error("Erreur presign image MinIO", value, err);
        return null;
      }
    }

    if (isMinioVideoKey(value)) {
      try {
        const { data } = await api.get("/api/minio/presign-download", {
          params: {
            bucket: "rxapa-videos",
            key: value,
          },
        });

        return data?.downloadUrl || null;
      } catch (err) {
        console.error("Erreur presign vidéo MinIO", value, err);
        return null;
      }
    }

    if (value.startsWith("/images/")) {
      return `${apiBase}${value}`;
    }

    return toAbsoluteUrl(value);
  };

  const openVideoPreview = (url) => {
    if (!url) return;

    setVideoPreviewUrl(url);
    setVideoPreviewOpen(true);
  };

  useEffect(() => {
    const fetchProgramme = async () => {
      try {
        setLoading(true);

        const { data } = await api.get(
          `/programme/programmes/${encodeURIComponent(programmeId)}`
        );

        setProgramme(data);
      } catch (err) {
        console.error("Erreur GET /programme/programmes/:id", err);
      } finally {
        setLoading(false);
      }
    };

    fetchProgramme();
  }, [programmeId]);

  useEffect(() => {
    const loadFrequencies = async () => {
      if (!programme?.cycles) return;

      const resultSession = {};

      for (const programmeCycle of programme.cycles) {
        const cycleKey = programmeCycle?.WeeklyCycle?.key;
        if (!cycleKey) continue;

        try {
          const { data } = await api.get(
            `/cycle/${encodeURIComponent(cycleKey)}`
          );

          const items = buildCycleItemsFromData(data);
          summarizeCycleFrequency(items);

          resultSession[cycleKey] = items.reduce((acc, item) => {
            if (!item?.baseSessionId || !item?.frequency) return acc;
            acc[String(item.baseSessionId)] = item.frequency;
            acc[String(item.title || "").trim().toLowerCase()] = item.frequency;
            return acc;
          }, {});
        } catch (err) {
          console.error("Erreur chargement fréquence cycle", err);
        }
      }

      setSessionFrequencies(resultSession);
    };

    loadFrequencies();
  }, [programme]);

  useEffect(() => {
    const fetchProgrammeContent = async () => {
      if (!programmeId) return;

      try {
        setLoadingContent(true);

        const { data } = await api.get(
          `/programme/programmes/${encodeURIComponent(programmeId)}/content`
        );

        setProgrammeContent(data);
      } catch (err) {
        console.error("Erreur GET /programme/programmes/:id/content", err);
      } finally {
        setLoadingContent(false);
      }
    };

    fetchProgrammeContent();
  }, [programmeId]);

  useEffect(() => {
    const loadResolvedMedia = async () => {
      if (!programmeContent?.cycles) {
        setResolvedMediaUrls({});
        return;
      }

      const values = new Set();

      (programmeContent.cycles || []).forEach((cycle) => {
        const sessions = cycle?.weeklyCycle?.sessions || [];

        sessions.forEach((session) => {
          (session.blocks || []).forEach((block) => {
            (block.exercises || []).forEach((exercise) => {
              if (exercise.imageUrl) values.add(exercise.imageUrl);
              if (exercise.videoUrl) values.add(exercise.videoUrl);
            });
          });
        });
      });

      const entries = await Promise.all(
        Array.from(values).map(async (value) => {
          const resolved = await resolveMediaUrl(value);
          return [value, resolved];
        })
      );

      setResolvedMediaUrls(Object.fromEntries(entries));
    };

    loadResolvedMedia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programmeContent]);

  if (loading) {
    return <div style={{ padding: 24 }}>Chargement du programme…</div>;
  }

  if (!programme) {
    return (
      <div style={{ padding: 24 }}>
        Programme introuvable.
        <br />
        <Button style={{ marginTop: 12 }} onClick={() => navigate(-1)}>
          Retour
        </Button>
      </div>
    );
  }

  const patientLabel =
    patientNameFromState || `Patient #${programme.PatientId ?? "?"}`;

  return (
    <>
      <div style={{ padding: 24 }}>
        <Space style={{ marginBottom: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            Retour
          </Button>

          <Title level={3} style={{ margin: 0 }}>
            Programme de {patientLabel}
          </Title>
        </Space>

        <Card title="Résumé du programme" style={{ marginBottom: 16 }}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Nom">
              {programme.name}
            </Descriptions.Item>

            {programme.description && (
              <Descriptions.Item label="Description">
                {programme.description}
              </Descriptions.Item>
            )}

            <Descriptions.Item label="Période">
              {programme.startDate || "—"} → {programme.endDate || "—"}
            </Descriptions.Item>

            <Descriptions.Item label="Durée">
              {programme.durationWeeks
                ? `${programme.durationWeeks} semaine${
                    programme.durationWeeks > 1 ? "s" : ""
                  }`
                : "—"}
            </Descriptions.Item>

            <Descriptions.Item label="Statut">
              <Tag color={STATUS_COLOR[programme.status] || "default"}>
                {programme.status}
              </Tag>
            </Descriptions.Item>

            {programme.notes && (
              <Descriptions.Item label="Notes">
                <Text>{programme.notes}</Text>
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>

        <Card
          title="Vue d’ensemble du contenu du programme"
          style={{ marginTop: 24 }}
        >
          {loadingContent && (
            <Text>Chargement du contenu du programme…</Text>
          )}

          {!loadingContent && !programmeContent && (
            <Text type="secondary">
              Aucun contenu détaillé disponible pour ce programme.
            </Text>
          )}

          {!loadingContent &&
            programmeContent &&
            (programmeContent.cycles || []).length === 0 && (
              <Text type="secondary">
                Aucun cycle détaillé à afficher pour ce programme.
              </Text>
            )}

          {!loadingContent &&
            programmeContent &&
            (programmeContent.cycles || []).map((cycle, index) => {
              const weeklyCycle = cycle.weeklyCycle || {};
              const sessions = weeklyCycle.sessions || [];

              const metaProgrammeCycle =
                weeklyCycle.key && (programme?.cycles || []).length > 0
                  ? (programme.cycles || []).find(
                      (pc) => pc?.WeeklyCycle?.key === weeklyCycle.key
                    )
                  : null;

              const startDate =
                metaProgrammeCycle?.startDate || cycle?.startDate || null;
              const endDate =
                metaProgrammeCycle?.endDate || cycle?.endDate || null;

              const cycleDurationLabel = getCycleDurationLabel(
                startDate,
                endDate
              );

              return (
                <Card
                  key={cycle.order || index}
                  type="inner"
                  style={{ marginBottom: 16 }}
                  title={`${index + 1}. ${
                    weeklyCycle.name || "Cycle sans nom"
                  }`}
                >
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ color: "#666" }}>
                      Période du cycle : {startDate || "?"} →{" "}
                      {endDate || "?"}
                    </div>

                    {cycleDurationLabel && (
                      <div style={{ color: "#666" }}>
                        Durée du cycle : {cycleDurationLabel}
                      </div>
                    )}
                  </div>

                  {sessions.length === 0 && (
                    <Text type="secondary">
                      Aucune séance définie dans ce cycle.
                    </Text>
                  )}

                  {sessions.map((session) => {
                    const freqForSession =
                      (weeklyCycle.key &&
                        sessionFrequencies?.[weeklyCycle.key]?.[
                          String(session.name || "")
                            .trim()
                            .toLowerCase()
                        ]) ||
                      (weeklyCycle.key &&
                        sessionFrequencies?.[weeklyCycle.key]?.[
                          String(session.id)
                        ]) ||
                      (session.days?.length
                        ? {
                            kind: "custom",
                            days: session.days,
                            timeOfDay: session.timeOfDay || null,
                          }
                        : null);

                    return (
                      <Card
                        key={session.id}
                        size="small"
                        style={{ marginBottom: 12 }}
                        title={
                          <Space size="small" wrap>
                            <span>{session.name}</span>

                            {session.timeOfDay && (
                              <Tag color="blue">
                                {TIME_OF_DAY_LABELS[session.timeOfDay] ||
                                  session.timeOfDay}
                              </Tag>
                            )}

                            {freqForSession && (
                              <FrequencyBadge frequency={freqForSession} />
                            )}
                          </Space>
                        }
                      >
                        {session.blocks.length === 0 && (
                          <Text type="secondary">
                            Aucun bloc dans cette séance.
                          </Text>
                        )}

                        {session.blocks.length > 0 && (
                          <div
                            style={{
                              display: "grid",
                              gap: 12,
                              gridTemplateColumns:
                                "repeat(auto-fit, minmax(220px, 1fr))",
                            }}
                          >
                            {session.blocks.map((block) => (
                              <Card
                                key={block.id}
                                size="small"
                                title={block.name}
                                style={{ marginBottom: 8 }}
                              >
                                {block.description && (
                                  <p
                                    style={{
                                      marginBottom: 8,
                                      color: "#666",
                                    }}
                                  >
                                    {block.description}
                                  </p>
                                )}

                                {block.exercises.length === 0 && (
                                  <Text type="secondary">
                                    Aucun exercice dans ce bloc.
                                  </Text>
                                )}

                                {block.exercises.length > 0 && (
                                  <List
                                    size="small"
                                    dataSource={block.exercises}
                                    renderItem={(exercise) => {
                                      const imgSrc =
                                        resolvedMediaUrls[exercise.imageUrl] ||
                                        null;

                                      const videoSrc =
                                        resolvedMediaUrls[exercise.videoUrl] ||
                                        null;

                                      return (
                                        <List.Item>
                                          <Space align="start" size={12}>
                                            {imgSrc && (
                                              <Image
                                                width={72}
                                                height={72}
                                                src={imgSrc}
                                                alt={exercise.name}
                                                style={{
                                                  borderRadius: 10,
                                                  objectFit: "cover",
                                                }}
                                                preview={{
                                                  mask: "Aperçu",
                                                }}
                                              />
                                            )}

                                            <Space
                                              direction="vertical"
                                              size={2}
                                            >
                                              <Text strong>
                                                {exercise.name}
                                              </Text>

                                              {exercise.description && (
                                                <Text type="secondary">
                                                  {exercise.description}
                                                </Text>
                                              )}

                                              {videoSrc && (
                                                <Button
                                                  type="link"
                                                  size="small"
                                                  icon={<PlayCircleOutlined />}
                                                  onClick={() =>
                                                    openVideoPreview(videoSrc)
                                                  }
                                                  style={{
                                                    padding: 0,
                                                    height: "auto",
                                                    justifyContent:
                                                      "flex-start",
                                                  }}
                                                >
                                                  Voir la vidéo
                                                </Button>
                                              )}
                                            </Space>
                                          </Space>
                                        </List.Item>
                                      );
                                    }}
                                  />
                                )}
                              </Card>
                            ))}
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </Card>
              );
            })}
        </Card>
      </div>

      <Modal
        open={videoPreviewOpen}
        onCancel={() => {
          setVideoPreviewOpen(false);
          setVideoPreviewUrl(null);
        }}
        footer={null}
        title="Aperçu vidéo"
        width={900}
        destroyOnClose
      >
        {videoPreviewUrl ? (
          <div style={{ aspectRatio: "16/9" }}>
            <ReactPlayer
              url={videoPreviewUrl}
              controls
              width="100%"
              height="100%"
            />
          </div>
        ) : (
          <Empty description="Impossible d’afficher la vidéo." />
        )}
      </Modal>
    </>
  );
}